import { z } from "zod";
import { restaurantStatusSchema } from "./enums";
import { systemMetricsSchema } from "./metrics";
import { billingDashboardSchema } from "./platform-billing";

/**
 * Platform operations for the admin app (P11-T14, P11-T15; AD-08). See
 * docs/architecture/api.md, System (admin).
 */

const count = z.number().int().nonnegative();

/** Days of orders and bills the dashboard shows, today included. */
export const DASHBOARD_DAYS = 14;

export const failingPrinterSchema = z.object({
	restaurant: z.object({ id: z.uuid(), name: z.string(), slug: z.string() }),
	printerId: z.uuid(),
	printerName: z.string(),
	/** Jobs that failed in the last 24 hours. */
	failedJobs: count,
	lastError: z.string().nullable(),
	lastFailedAt: z.iso.datetime().nullable(),
	/** A station's last bad report (unreachable, paper out, cover open, offline), if any. */
	state: z.string().nullable(),
	lastReportAt: z.iso.datetime().nullable(),
});
export type FailingPrinter = z.infer<typeof failingPrinterSchema>;

/** `admin.system.dashboard`. Dates are India's business dates. */
export const adminDashboardSchema = z.object({
	today: z.iso.date(),
	restaurants: z.record(restaurantStatusSchema, count),
	/** Restaurants with at least one order today. */
	activeToday: count,
	/** The last `DASHBOARD_DAYS` days, oldest first, across restaurants. */
	days: z.array(z.object({ date: z.iso.date(), orders: count, bills: count })),
	/** Subscriptions due and overdue, as Billing > Overview has them. */
	billing: billingDashboardSchema,
	/** This API process's health: requests, sockets, queues, outbox, print jobs. */
	health: systemMetricsSchema,
	/** Printers failing in the last 24 hours, newest trouble first. */
	failingPrinters: z.array(failingPrinterSchema),
});
export type AdminDashboard = z.infer<typeof adminDashboardSchema>;

/** `admin.system.restaurantActivity` (P11-T15): one restaurant's recent activity. */
export const restaurantActivitySchema = z.object({
	lastOrderAt: z.iso.datetime().nullable(),
	lastBillAt: z.iso.datetime().nullable(),
	/** The last time a member opened the staff app here, and who. */
	lastSeen: z
		.object({ at: z.iso.datetime(), name: z.string().nullable(), email: z.string().nullable() })
		.nullable(),
	/** Apps and versions members opened in the last 30 days, most members first. */
	apps: z.array(z.object({ app: z.string(), members: count, lastSeenAt: z.iso.datetime() })),
	printers: z.array(
		z.object({
			id: z.uuid(),
			name: z.string(),
			connection: z.string(),
			isActive: z.boolean(),
			/** Each device's last report, newest first. */
			stations: z.array(
				z.object({
					deviceName: z.string().nullable(),
					isReachable: z.boolean(),
					state: z.string(),
					lastCheckedAt: z.iso.datetime(),
				}),
			),
			failedLast24h: count,
			lastJob: z
				.object({
					status: z.string(),
					lastError: z.string().nullable(),
					at: z.iso.datetime(),
				})
				.nullable(),
		}),
	),
});
export type RestaurantActivity = z.infer<typeof restaurantActivitySchema>;
